import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from './PsModals';
import { fmtMoney, fmtQty, psApi } from './psApi';

// Lock / unlock dialog and the History log (2026-11-02, user request).
// Every change to a product structure is a row in `bom_link_events` (server: GET /history).

const LOCK_PATH = <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>;
const UNLOCK_PATH = <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 7.5-2" /></>;

export const LockGlyph = ({ open, size = 16, stroke = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{open ? UNLOCK_PATH : LOCK_PATH}</svg>
);

/** "2026-11-02 10:22 UTC" — every stamp here is UTC (Note #50). */
export const fmtStamp = (s) => (s ? `${String(s).replace('T', ' ').slice(0, 16)} UTC` : '');

const Check = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>
);
const Cross = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"
    aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

/** Lock or unlock one part in this product — says what it means, takes an optional comment. */
export function LockModal({ row, product, unlock, onClose, onConfirm }) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const whole = row.depth === 0;
  const what = whole ? `The whole ${row.key} structure` : `${row.key} and everything under it`;
  const go = async () => {
    setBusy(true);
    setError('');
    try { await onConfirm(comment.trim()); } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Modal title={unlock ? `Unlock ${row.key}` : `Lock ${row.key}`} onClose={onClose} footer={<>
      <button type="button" className="ps-btn ps-btn--ghost" onClick={onClose}>Cancel</button>
      <button type="button" className={'ps-btn ' + (unlock ? 'ps-btn--unlock' : 'ps-btn--lock')} disabled={busy} onClick={go} autoFocus>
        <LockGlyph open={unlock} size={15} />{busy ? (unlock ? 'Unlocking…' : 'Locking…') : (unlock ? 'Unlock' : 'Lock')}
      </button>
    </>}>
      <div className={'ps-lk-hero' + (unlock ? ' ps-lk-hero--open' : '')}>
        <span className="ps-lk-badge"><LockGlyph open={unlock} size={26} stroke={2.2} /></span>
        <div>
          <div className="ps-lk-title">{unlock ? `${what} will be open for changes again` : `${what} will be locked for changes`}</div>
          <div className="ps-lk-sub">{row.name ? `${row.key} · ${row.name}` : row.key}{!whole && product ? ` — in ${product}` : ''}</div>
        </div>
      </div>

      {unlock ? (
        <div className="ps-lk-who">
          <div><span className="ps-muted">Locked by</span> <b>{row.locked_by || 'unknown'}</b>{row.locked_at && <> <span className="ps-muted">on</span> {fmtStamp(row.locked_at)}</>}</div>
          {row.lock_comment && <blockquote className="ps-lk-quote">{row.lock_comment}</blockquote>}
          <p className="ps-muted ps-small" style={{ margin: 0 }}>Anyone with Edit rights on parts can then change quantities, add, move and remove parts again.</p>
        </div>
      ) : (
        <div className="ps-lk-grid">
          <div className="ps-lk-col ps-lk-col--no">
            <div className="ps-lk-h">Can&rsquo;t be changed on main</div>
            <ul>
              <li><Cross />Quantity, find no. and ref. des.</li>
              <li><Cross />Adding, removing or moving parts</li>
              <li><Cross />Merging a branch that changes it</li>
            </ul>
          </div>
          <div className="ps-lk-col ps-lk-col--yes">
            <div className="ps-lk-h">Still allowed</div>
            <ul>
              <li><Check />Unit cost (saved in Orcanos)</li>
              <li><Check />Taking baselines</li>
              <li><Check />Making branches and editing them</li>
            </ul>
          </div>
        </div>
      )}
      {!unlock && row.shared > 1 && (
        <div className="ps-banner ps-banner--warn" style={{ margin: 0 }}>
          {row.key} is shared by {row.shared} products — its lines can&rsquo;t be changed from those products either.
        </div>
      )}
      <label className="ps-field">Comment <span className="ps-muted ps-small" style={{ fontWeight: 400 }}>(optional — kept in the History log)</span>
        <textarea className="ps-ta" rows={3} value={comment} maxLength={400} onChange={(e) => setComment(e.target.value)}
          placeholder={unlock ? 'e.g. Opened for ECO-1042' : 'e.g. Design review passed — frozen for release 2.0'} />
      </label>
      {error && <div className="ps-error" role="alert">{error}</div>}
    </Modal>
  );
}

/** ⋮ above the grid — more actions on the selected product. */
export function MoreMenu({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const key = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', key); };
  }, [open]);
  return (
    <div className="ps-more" ref={ref}>
      <button type="button" className={'ps-ibtn ps-more-btn' + (open ? ' ps-more-btn--on' : '')} aria-haspopup="menu" aria-expanded={open}
        aria-label="More actions" title="More actions" onClick={() => setOpen((o) => !o)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="5" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="12" cy="19" r="1.9" /></svg>
      </button>
      {open && (
        <div className="ps-more-menu" role="menu">
          {items.filter(Boolean).map((it) => (
            <button key={it.label} type="button" role="menuitem" className="ps-more-item" onClick={() => { setOpen(false); it.onClick(); }}>
              {it.icon}<span><b>{it.label}</b>{it.hint && <small>{it.hint}</small>}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export const HistoryIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l3 2" /></svg>
);

// ─── History log ─────────────────────────────────────────────────────────────

const ACT = {
  add: ['Added', 'add', 'structure'], update: ['Changed', 'chg', 'structure'], move: ['Moved', 'move', 'structure'],
  remove: ['Removed', 'del', 'structure'], use_shared: ['Shared again', 'chg', 'structure'],
  lock: ['Locked', 'lock', 'lock'], unlock: ['Unlocked', 'unlock', 'lock'], cost: ['Cost', 'cost', 'cost'],
  baseline: ['Baseline', 'base', 'version'], delete_baseline: ['Baseline deleted', 'del', 'version'],
  branch: ['Branch created', 'branch', 'version'], merge: ['Merged', 'branch', 'version'],
  close_branch: ['Branch closed', 'branch', 'version'], delete_branch: ['Branch deleted', 'del', 'version'],
  add_product: ['Product added', 'add', 'structure'], remove_product: ['Product removed', 'del', 'structure'],
};
const VIA = { merge: 'merge', import: 'file import' };
const FIELD = { qty: 'Qty', find_no: 'Find', ref_des: 'Ref. des.', note: 'Note', ord: 'Order', child: 'Part' };
const GROUPS = [['all', 'All'], ['structure', 'Structure'], ['lock', 'Locks'], ['cost', 'Cost'], ['version', 'Baselines & branches']];

/** "merge:4:update" → { base: 'update', via: 'merge' }. */
function parseAction(a) {
  const parts = String(a || '').split(':');
  if (parts.length >= 3) return { base: parts[parts.length - 1], via: VIA[parts[0]] || parts[0] };
  return { base: parts[0], via: '' };
}

const val = (k, v) => (k === 'qty' ? fmtQty(v) : (v === '' || v == null ? '—' : String(v)));

/** One line of plain text saying what changed — also what the CSV holds. */
export function describe(e) {
  const { base } = parseAction(e.action);
  const b = e.before || {};
  const a = e.after || {};
  const par = e.parent?.key || '';
  switch (base) {
    case 'add': {
      const extra = [a.find_no && `find ${a.find_no}`, a.ref_des && `ref. des. ${a.ref_des}`].filter(Boolean).join(', ');
      return `× ${fmtQty(a.qty ?? 1)}${par ? ` under ${par}` : ''}${extra ? ` (${extra})` : ''}`;
    }
    case 'update': {
      const keys = Object.keys(a).filter((k) => FIELD[k] && String(b[k] ?? '') !== String(a[k] ?? ''));
      return keys.map((k) => `${FIELD[k]} ${val(k, b[k])} → ${val(k, a[k])}`).join(' · ') || (par ? `in ${par}` : '');
    }
    case 'move': return `from ${e.from?.key || '?'} to ${par || '?'}`;
    case 'remove': return `from ${par || '?'}${b.qty != null ? ` (was × ${fmtQty(b.qty)})` : ''}`;
    case 'use_shared': return `${par} uses the shared version again`;
    case 'lock': return a.comment || '';
    case 'unlock': return a.comment || '';
    case 'cost': return `${fmtMoney(b.cost)} → ${fmtMoney(a.cost)}${a.field ? ` (Orcanos “${a.field}”)` : ''}`;
    case 'baseline': return `“${a.name}” · ${a.parts ?? '?'} parts · ${fmtMoney(a.cost)}`;
    case 'merge': return `“${a.name}” · ${a.changes} change${a.changes === 1 ? '' : 's'}`;
    case 'branch': return `“${a.name}”${a.kind === 'variant' ? ' · variant' : ''}`;
    case 'close_branch': return `“${a.name}”`;
    case 'delete_baseline': case 'delete_branch': return `“${b.name}”`;
    default: return '';
  }
}

function csvOf(rows) {
  const esc = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const head = ['When (UTC)', 'Who', 'Action', 'Via', 'Key', 'Name', 'Details'];
  return [head, ...rows.map((e) => {
    const { base, via } = parseAction(e.action);
    return [String(e.at || '').replace('T', ' ').slice(0, 19), e.by, (ACT[base] || [base])[0], via, e.part?.key || '', e.part?.name || '', describe(e)];
  })].map((r) => r.map(esc).join(',')).join('\r\n');
}

export function HistoryModal({ root, product, onClose, onAuth }) {
  const [rows, setRows] = useState([]);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [group, setGroup] = useState('all');
  const [text, setText] = useState('');

  const load = useCallback(async (beforeId) => {
    setBusy(true);
    setError('');
    try {
      const r = await psApi.history(root, beforeId);
      setRows((old) => (beforeId ? [...old, ...r.events] : r.events));
      setMore(r.more);
    } catch (e) {
      if (!onAuth?.(e)) setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [root, onAuth]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { all: rows.length };
    rows.forEach((e) => { const g = (ACT[parseAction(e.action).base] || [])[2] || 'structure'; c[g] = (c[g] || 0) + 1; });
    return c;
  }, [rows]);
  const shown = useMemo(() => {
    const t = text.trim().toLowerCase();
    return rows.filter((e) => {
      const g = (ACT[parseAction(e.action).base] || [])[2] || 'structure';
      if (group !== 'all' && g !== group) return false;
      if (!t) return true;
      return [e.by, e.part?.key, e.part?.name, e.parent?.key, describe(e)].some((s) => String(s || '').toLowerCase().includes(t));
    });
  }, [rows, group, text]);

  const download = () => {
    const blob = new Blob(['﻿' + csvOf(shown)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `history-${(product || root).replace(/[^\w.-]+/g, '_')}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <Modal title={`History log — ${product || root}`} wide onClose={onClose} footer={<>
      <span className="ps-muted ps-small" style={{ marginRight: 'auto' }}>
        {shown.length} of {rows.length}{more ? '+' : ''} change{rows.length === 1 ? '' : 's'} · times in UTC</span>
      <button type="button" className="ps-btn ps-btn--ghost" disabled={!shown.length} onClick={download}>Download CSV</button>
      <button type="button" className="ps-btn" onClick={onClose}>Close</button>
    </>}>
      <div className="ps-hx-bar">
        <div className="ps-hx-chips" role="tablist" aria-label="Kind of change">
          {GROUPS.map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={group === k}
              className={'ps-hx-chip' + (group === k ? ' ps-hx-chip--on' : '')} onClick={() => setGroup(k)}>
              {l}<span className="ps-hx-n">{counts[k] || 0}</span>
            </button>
          ))}
        </div>
        <input className="ps-search ps-hx-search" type="search" placeholder="Search part, person or change" aria-label="Search the history"
          value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      {error && <div className="ps-error" role="alert">{error}</div>}
      <div className="ps-hx-tw">
        <table className="ps-hx">
          <thead><tr><th>When</th><th>Who</th><th>Change</th><th>Part</th><th>Details</th></tr></thead>
          <tbody>
            {shown.map((e) => {
              const { base, via } = parseAction(e.action);
              const [label, tone] = ACT[base] || [base, 'chg'];
              const d = describe(e);
              const isNote = base === 'lock' || base === 'unlock';
              return (
                <tr key={e.id}>
                  <td className="ps-hx-when">{fmtStamp(e.at).replace(' UTC', '')}</td>
                  <td className="ps-hx-who">{e.by || '—'}</td>
                  <td>
                    <span className={'ps-hx-act ps-hx-act--' + tone}>
                      {(base === 'lock' || base === 'unlock') && <LockGlyph open={base === 'unlock'} size={12} stroke={2.4} />}{label}
                    </span>
                    {via && <span className="ps-hx-via">via {via}</span>}
                  </td>
                  <td className="ps-hx-part">
                    {e.part ? <><b>{e.part.key}</b>{e.part.name && <span>{e.part.name}</span>}</> : <span className="ps-muted">—</span>}
                  </td>
                  <td className={'ps-hx-det' + (isNote && d ? ' ps-hx-det--note' : '')}>{d || <span className="ps-muted">—</span>}</td>
                </tr>
              );
            })}
            {!busy && !shown.length && (
              <tr><td colSpan={5} className="ps-hx-empty">{rows.length ? 'Nothing matches.' : 'No changes recorded for this product yet.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {busy && <div className="ps-muted ps-small">Loading…</div>}
      {more && !busy && (
        <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm" style={{ alignSelf: 'center' }}
          onClick={() => load(rows[rows.length - 1]?.id)}>Load older changes</button>
      )}
    </Modal>
  );
}
